import { tokens } from '@/theme'

interface Props {
  /** Category/eyebrow — small uppercase label sitting above the headline */
  eyebrow: string
  /** Top line of headline (rendered at ~70% opacity per brand guideline) */
  headlineTop: string
  /** Bottom line of headline (full opacity — the accent line) */
  headlineBottom: string
  /** Supporting paragraph — one or two sentences */
  lede?: string
  /** Right-side featured panel content */
  featured?: React.ReactNode
}

export function Hero({ eyebrow, headlineTop, headlineBottom, lede, featured }: Props) {
  return (
    <section
      style={{
        position: 'relative',
        background: tokens.paper,
        paddingTop: 56,
        paddingBottom: 72,
        borderBottom: `1px solid ${tokens.line}`,
        overflow: 'hidden',
      }}
    >
      {/* Warm paper gradient + subtle texture */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse at 90% 0%, rgba(153,130,66,0.07) 0%, transparent 55%),
            linear-gradient(180deg, ${tokens.paper} 0%, ${tokens.paper2} 100%)
          `,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'relative',
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `0 ${tokens.containerPadX}px`,
          display: 'grid',
          gridTemplateColumns: featured ? 'minmax(0, 1.25fr) minmax(0, 1fr)' : '1fr',
          gap: 56,
          alignItems: 'stretch',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {/* Gold accent bar */}
          <div
            style={{
              width: 56,
              height: 2,
              background: tokens.gold,
              marginBottom: 24,
              transformOrigin: 'left',
              animation: `barGrow ${tokens.durSlow} ${tokens.easeStandard} both`,
            }}
          />

          <div
            style={{
              fontSize: 11.5,
              fontWeight: tokens.weightDemi,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: tokens.ink3,
              marginBottom: 22,
              animation: `slideUp ${tokens.durSlow} ${tokens.easeStandard} both`,
              animationDelay: '80ms',
            }}
          >
            {eyebrow}
          </div>

          {/* Split headline per brand guidelines */}
          <h1
            style={{
              fontSize: tokens.sizeDisplay1,
              fontWeight: tokens.weightDemi,
              letterSpacing: '-0.02em',
              lineHeight: 1.02,
              color: tokens.ink,
              margin: 0,
              maxWidth: 780,
            }}
          >
            <span
              style={{
                display: 'block',
                opacity: 0.62,
                animation: `slideUp ${tokens.durSlow} ${tokens.easeStandard} both`,
                animationDelay: '140ms',
              }}
            >
              {headlineTop}
            </span>
            <span
              style={{
                display: 'block',
                animation: `slideUp ${tokens.durSlow} ${tokens.easeStandard} both`,
                animationDelay: '220ms',
              }}
            >
              {headlineBottom}
            </span>
          </h1>

          {lede && (
            <p
              style={{
                fontSize: tokens.sizeBodyL,
                lineHeight: 1.65,
                color: tokens.ink2,
                marginTop: 28,
                maxWidth: 620,
                fontWeight: tokens.weightBook,
                animation: `slideUp ${tokens.durSlow} ${tokens.easeStandard} both`,
                animationDelay: '300ms',
              }}
            >
              {lede}
            </p>
          )}
        </div>

        {featured && (
          <div
            style={{
              position: 'relative',
              animation: `slideUp ${tokens.durSlow} ${tokens.easeStandard} both`,
              animationDelay: '360ms',
            }}
          >
            {featured}
          </div>
        )}
      </div>
    </section>
  )
}
